import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  timeout: 300000,
});

// -----------------------------------------------------------------------------
// Safe error-message extractor. Backend errors put their payload in
// `response.data.detail`, which may be:
//   • a string        → return it
//   • a plan-gate obj  → { reason, plan, message, ... } → return `.message`
//   • a 422 array      → [{ msg, loc, ... }]            → return first `.msg`
// Passing a raw object/array to React (e.g. toast.error) crashes the app with
// "Objects are not valid as a React child", so ALL call sites must go through
// this helper to guarantee a string.
// -----------------------------------------------------------------------------
export function errMsg(err, fallback = 'Something went wrong. Please try again.') {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    const first = d[0];
    if (first && typeof first.msg === 'string') return first.msg;
    return fallback;
  }
  if (d && typeof d === 'object' && typeof d.message === 'string') return d.message;
  return fallback;
}

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('ageorush_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// -----------------------------------------------------------------------------
// Global 402 (Payment Required) handler — the backend's plan_gate returns 402
// with a structured `detail` object. We surface it as a rich toast with an
// "Upgrade" CTA so users hit a limit and immediately have a path forward.
// -----------------------------------------------------------------------------
let __lastUpgradeToast = 0;
function showUpgradeToast(detail) {
  const now = Date.now();
  // Debounce identical 402s that fire in quick succession (e.g. list retries)
  if (now - __lastUpgradeToast < 1500) return;
  __lastUpgradeToast = now;

  const msg = (detail && detail.message) || 'Your current plan does not allow this action.';
  toast.error(msg, {
    id: 'plan-upgrade',
    duration: 7000,
    action: {
      label: 'Upgrade',
      // Stay inside the authenticated app (not the public marketing site) so
      // the user never feels "logged out" mid-upgrade.
      onClick: () => { window.location.href = '/app/billing/upgrade'; },
    },
  });
}

api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    if (status === 401) {
      const path = window.location.pathname;
      if (path.startsWith('/app')) {
        localStorage.removeItem('ageorush_token');
        window.location.href = '/login';
      }
    } else if (status === 402) {
      // Structured plan-gate response from backend
      showUpgradeToast(err.response.data && err.response.data.detail);
    }
    return Promise.reject(err);
  },
);
